/**
 * HotkeyContext - P2.3 Global Hotkey System
 * 
 * Provides global keyboard shortcuts for Granullo:
 * - Esc: Exit/close (modal > drawer > overlay > no-op)
 * - Cmd/Ctrl+Z: Undo last eligible action
 * - Cmd/Ctrl+K: Open command palette
 * 
 * Accessible and non-invasive. Respects focus states and input fields.
 */

import React, { createContext, useContext, useCallback, useEffect, useState, useRef } from 'react';

const HotkeyContext = createContext(null);

// Hotkey definitions
export const HOTKEYS = {
  UNDO: { key: 'z', meta: true, description: 'Undo last action' },
  COMMAND_PALETTE: { key: 'k', meta: true, description: 'Open command palette' },
  ESCAPE: { key: 'Escape', description: 'Close / Exit' },
};

/**
 * HotkeyProvider - Handles global keyboard shortcuts
 */
export function HotkeyProvider({ children }) {
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [activeModals, setActiveModals] = useState([]);
  const [activeDrawers, setActiveDrawers] = useState([]);
  const [hasSeenHotkeyHint, setHasSeenHotkeyHint] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('granullo_hotkey_hint_seen') === 'true';
    }
    return false;
  });
  
  // Store undo function reference
  const undoFnRef = useRef(null);
  const canUndoRef = useRef(false);

  // Register undo function from UndoContext
  const registerUndo = useCallback((undoFn, canUndo) => {
    undoFnRef.current = undoFn;
    canUndoRef.current = canUndo;
  }, []);

  // Register a modal as active
  const registerModal = useCallback((id) => {
    setActiveModals((prev) => [...prev, id]);
    return () => setActiveModals((prev) => prev.filter((m) => m !== id));
  }, []);

  // Register a drawer as active
  const registerDrawer = useCallback((id) => {
    setActiveDrawers((prev) => [...prev, id]);
    return () => setActiveDrawers((prev) => prev.filter((d) => d !== id));
  }, []);

  // Open command palette
  const openCommandPalette = useCallback(() => {
    setIsCommandPaletteOpen(true);
  }, []);

  // Close command palette
  const closeCommandPalette = useCallback(() => {
    setIsCommandPaletteOpen(false);
  }, []);

  // Toggle command palette
  const toggleCommandPalette = useCallback(() => {
    setIsCommandPaletteOpen((prev) => !prev);
  }, []);

  // Mark hotkey hint as seen
  const markHotkeyHintSeen = useCallback(() => {
    setHasSeenHotkeyHint(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem('granullo_hotkey_hint_seen', 'true');
    }
  }, []);

  // Check if we should ignore hotkeys (e.g., when typing in input)
  const shouldIgnoreHotkey = useCallback((e) => {
    const target = e.target;
    const tagName = target.tagName.toLowerCase();
    
    // Ignore when typing in form elements
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
      // Allow Escape to work in inputs for closing
      if (e.key === 'Escape') {
        return false;
      }
      return true;
    }
    
    // Ignore if target is contentEditable
    if (target.isContentEditable) {
      if (e.key === 'Escape') {
        return false;
      }
      return true;
    }
    
    return false;
  }, []);

  // Global keydown handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if we should ignore this event
      if (shouldIgnoreHotkey(e)) {
        return;
      }

      const isMeta = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();

      // Cmd/Ctrl + K: Command Palette
      if (isMeta && key === 'k') {
        e.preventDefault();
        toggleCommandPalette();
        return;
      }

      // Cmd/Ctrl + Z: Undo
      if (isMeta && key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndoRef.current && undoFnRef.current) {
          undoFnRef.current();
        }
        return;
      }

      // Escape: Close in priority order
      if (e.key === 'Escape') {
        // Priority 1: Close command palette
        if (isCommandPaletteOpen) {
          e.preventDefault();
          closeCommandPalette();
          return;
        }

        // Priority 2: Close active modal (handled by modal components)
        // Priority 3: Close active drawer (handled by drawer components)
        // Priority 4: No-op (don't navigate away unexpectedly)
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shouldIgnoreHotkey, toggleCommandPalette, closeCommandPalette, isCommandPaletteOpen]);

  const value = {
    // Command palette
    isCommandPaletteOpen,
    openCommandPalette,
    closeCommandPalette,
    toggleCommandPalette,
    
    // Modal/drawer registration
    registerModal,
    registerDrawer,
    activeModals,
    activeDrawers,
    
    // Hotkey hint
    hasSeenHotkeyHint,
    markHotkeyHintSeen,
    
    // Undo registration
    registerUndo,
    
    // Hotkey definitions
    HOTKEYS,
  };

  return (
    <HotkeyContext.Provider value={value}>
      {children}
    </HotkeyContext.Provider>
  );
}

/**
 * useHotkeys hook - Access hotkey functionality
 */
export function useHotkeys() {
  const context = useContext(HotkeyContext);
  if (!context) {
    throw new Error('useHotkeys must be used within a HotkeyProvider');
  }
  return context;
}

/**
 * Get the platform-specific meta key label
 */
export function getMetaKeyLabel() {
  if (typeof navigator !== 'undefined' && navigator.platform) {
    return navigator.platform.includes('Mac') ? '⌘' : 'Ctrl';
  }
  return 'Ctrl';
}

export default HotkeyContext;
